/**
 * BlueprintBuilder — the Conductor-builder canvas.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Top bar: name + description + Save draft + Save as Conductor │
 *   ├──────────┬─────────────────────────────────┬─────────────────┤
 *   │ Palette  │  ReactFlow canvas               │  Inspector      │
 *   │ (left)   │  (drop + connect + topo)        │  (right)        │
 *   └──────────┴─────────────────────────────────┴─────────────────┘
 *
 * Drag from Palette → canvas creates a new node whose data comes from
 * the Vault snapshot. Connect ports → validated against types; incom-
 * patible connections are rejected with a toast-style inline message.
 */

import '@xyflow/react/dist/style.css';

import {
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  type Connection,
  type Edge,
  type Node,
  type NodeTypes,
  type OnConnect,
  type IsValidConnection,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import { useCallback, useEffect, useMemo, useState, type DragEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { useToast } from '@/components/Toaster';
import type { Blueprint, GiSummary } from '@/api/types';
import { blueprint } from '@/api/endpoints/blueprint';
import { useVaultSnapshot, vaultKeys } from '@/hooks/useVault';
import { useQueryClient } from '@tanstack/react-query';

import { GiNode } from './GiNode';
import { Inspector } from './Inspector';
import { Palette, PALETTE_MIME } from './Palette';
import { portCompat, portTypeLabel, topologicalOrder } from './topology';
import type { BlueprintEdgeData, BlueprintNodeData } from './types';

const nodeTypes: NodeTypes = { gi: GiNode };

export function BlueprintBuilder() {
  return (
    <ReactFlowProvider>
      <BuilderInner />
    </ReactFlowProvider>
  );
}

function BuilderInner() {
  const { t } = useTranslation('blueprint');
  const { t: tCommon } = useTranslation();
  const toast = useToast();
  const vault = useVaultSnapshot();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search] = useSearchParams();
  const { screenToFlowPosition } = useReactFlow();

  const [name, setName] = useState('my-conductor');
  const [description, setDescription] = useState(
    'A Conductor that orchestrates two signed GIs in a linear pipeline.',
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selection, setSelection] = useState<{ nodeId?: string; edgeId?: string }>({});
  const [connectionHint, setConnectionHint] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  // If the Vault sent us here with `?add=<root_id>`, drop the GI in
  // the center as soon as the Vault query resolves.
  useEffect(() => {
    const add = search.get('add');
    if (!add || !vault.data) return;
    const gi = vault.data.gis.find((g) => g.root_id === add);
    if (!gi) return;
    addNodeFromGi(gi, { x: 260, y: 160 });
    // Clear the URL so a reload doesn't duplicate the node.
    window.history.replaceState({}, '', '/blueprint');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vault.data, search]);

  const addNodeFromGi = useCallback(
    (gi: GiSummary, position: { x: number; y: number }) => {
      // We need the full contract to know the port shapes. Pull it from
      // the Vault detail fetch synchronously via the snapshot's
      // indirection — for M1, use the summary's inputs/outputs; since
      // the Vault snapshot doesn't carry them, we synthesize a
      // placeholder single-port pair. A Phase 2 extension of the Vault
      // endpoint will return contracts.
      const data: BlueprintNodeData = {
        root_id: gi.root_id,
        version: gi.version,
        name: gi.name,
        domain: gi.domain,
        inputs: [
          { name: 'in', data_type: 'Document', required: true },
        ],
        outputs: [
          { name: 'out', data_type: 'Document', required: true },
        ],
      };
      const id = `n${Date.now().toString(36)}`;
      const node: Node = {
        id,
        type: 'gi',
        position,
        data: data as unknown as Record<string, unknown>,
      };
      setNodes((prev) => [...prev, node]);
    },
    [setNodes],
  );

  const isValid: IsValidConnection = useCallback(
    (conn) => {
      const source = nodes.find((n) => n.id === conn.source);
      const target = nodes.find((n) => n.id === conn.target);
      const r = portCompat(
        source,
        conn.sourceHandle,
        target,
        conn.targetHandle,
        edges,
      );
      setConnectionHint(
        r.ok
          ? null
          : r.source_type && r.target_type
            ? t('validation.incompatible', { src: r.source_type, tgt: r.target_type })
            : t('validation.blocked', { reason: r.reason ?? 'unknown' }),
      );
      return r.ok;
    },
    [nodes, edges, t],
  );

  const onConnect: OnConnect = useCallback(
    (conn: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...conn,
            data: { merge_policy: 'first', max_retries: 0 } satisfies BlueprintEdgeData,
            animated: false,
            style: { stroke: 'rgb(var(--c-spark))', strokeWidth: 2 },
          },
          eds,
        ),
      );
      setConnectionHint(null);
    },
    [setEdges],
  );

  // Drop handler — pull the palette drag payload, resolve the GI, add the node.
  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      const rootId = event.dataTransfer.getData(PALETTE_MIME);
      if (!rootId) return;
      const gi = vault.data?.gis.find((g) => g.root_id === rootId);
      if (!gi) return;
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      addNodeFromGi(gi, position);
    },
    [addNodeFromGi, vault.data, screenToFlowPosition],
  );

  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const deleteNode = useCallback(
    (id: string) => {
      setNodes((prev) => prev.filter((n) => n.id !== id));
      setEdges((prev) => prev.filter((e) => e.source !== id && e.target !== id));
      setSelection({});
    },
    [setNodes, setEdges],
  );
  const deleteEdge = useCallback(
    (id: string) => {
      setEdges((prev) => prev.filter((e) => e.id !== id));
      setSelection({});
    },
    [setEdges],
  );
  const onEdgeChange = useCallback(
    (id: string, changes: Partial<BlueprintEdgeData>) => {
      setEdges((prev) =>
        prev.map((e) =>
          e.id === id
            ? { ...e, data: { ...(e.data as unknown as BlueprintEdgeData), ...changes } as unknown as Record<string, unknown> }
            : e,
        ),
      );
    },
    [setEdges],
  );

  const topo = useMemo(() => topologicalOrder(nodes, edges), [nodes, edges]);

  const buildBlueprint = useCallback((): Blueprint => {
    return {
      name,
      description,
      error_policy: 'fail_fast',
      nodes: nodes.map((n) => {
        const d = n.data as unknown as BlueprintNodeData;
        return {
          id: n.id,
          root_id: d.root_id,
          version: d.version,
          name: d.name,
          domain: d.domain,
          inputs: d.inputs,
          outputs: d.outputs,
          position: { x: n.position.x, y: n.position.y },
        };
      }),
      edges: edges.map((e) => {
        const ed = (e.data ?? { merge_policy: 'first', max_retries: 0 }) as unknown as BlueprintEdgeData;
        return {
          id: e.id,
          source_node: e.source,
          source_port: e.sourceHandle ?? '',
          target_node: e.target,
          target_port: e.targetHandle ?? '',
          merge_policy: ed.merge_policy,
          max_retries: ed.max_retries,
        };
      }),
    };
  }, [name, description, nodes, edges]);

  const saveDraft = useCallback(async () => {
    await blueprint.saveDraft(buildBlueprint());
    setSavedTick(true);
    window.setTimeout(() => setSavedTick(false), 1600);
    toast.success(tCommon('toast.saved'));
  }, [buildBlueprint, toast, tCommon]);

  const publishAsConductor = useCallback(async () => {
    if (publishing || nodes.length === 0) return;
    setPublishing(true);
    try {
      const root_id = await blueprint.publish(buildBlueprint());
      void qc.invalidateQueries({ queryKey: vaultKeys.snapshot });
      toast.success(tCommon('toast.published'));
      navigate(`/gi/${root_id}`);
    } finally {
      setPublishing(false);
    }
  }, [publishing, nodes.length, buildBlueprint, qc, navigate, toast, tCommon]);

  const selectedNode = selection.nodeId ? nodes.find((n) => n.id === selection.nodeId) : undefined;
  const selectedEdge = selection.edgeId ? edges.find((e) => e.id === selection.edgeId) : undefined;

  return (
    <div className="flex h-[calc(100vh-10rem)] flex-col gap-4">
      <div className="rounded-xl border border-border bg-surface p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('topbar.name_placeholder')}
            className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm font-semibold text-ink focus-visible:border-spark"
          />
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('topbar.description_placeholder')}
            className="flex-[2] rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink-muted focus-visible:border-spark"
          />
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={saveDraft}>
              {savedTick ? t('topbar.saved') : t('topbar.save_draft')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={publishAsConductor}
              disabled={publishing || nodes.length === 0}
            >
              {publishing ? t('topbar.publishing') : t('topbar.publish')}
            </Button>
          </div>
        </div>
        {connectionHint ? (
          <p role="alert" className="mt-2 text-xs text-danger">
            {connectionHint}
          </p>
        ) : null}
      </div>

      <div className="grid flex-1 min-h-0 grid-cols-1 gap-4 md:grid-cols-[240px_minmax(0,1fr)_260px]">
        {/* Palette */}
        <aside className="rounded-xl border border-border bg-surface p-3">
          <Palette />
        </aside>

        {/* Canvas — forced LTR. The xyflow graph is a spatial
            diagram, not text content; industry flowchart tools do
            not mirror DAGs under RTL, and mirroring would cause a
            visual/logical mismatch between our `Position.Left`
            handles and the node's internal grid direction. Every
            description/title inside the canvas still localises
            correctly because those text runs carry their own
            bidi context from the translated strings. */}
        <section
          aria-label={t('title')}
          dir="ltr"
          className="relative rounded-xl border border-border bg-canvas"
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          {nodes.length === 0 ? (
            <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-1 text-center">
              <p className="text-sm font-medium text-ink">
                {t('canvas.empty_title')}
              </p>
              <p className="max-w-xs text-xs text-ink-muted">{t('canvas.empty_body')}</p>
            </div>
          ) : null}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            isValidConnection={isValid}
            nodeTypes={nodeTypes}
            fitView
            proOptions={{ hideAttribution: true }}
            onSelectionChange={(s) => {
              const n = s.nodes[0];
              const e = s.edges[0];
              const sel: { nodeId?: string; edgeId?: string } = {};
              if (n) sel.nodeId = n.id;
              if (e) sel.edgeId = e.id;
              setSelection(sel);
            }}
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="rgb(var(--c-border))" />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable />
          </ReactFlow>
        </section>

        {/* Right column — inspector + topo */}
        <aside className="flex flex-col gap-3">
          <div className="flex-1 min-h-[180px]">
            <Inspector
              selectedNode={selectedNode}
              selectedEdge={selectedEdge}
              onDeleteNode={deleteNode}
              onDeleteEdge={deleteEdge}
              onEdgeChange={onEdgeChange}
            />
          </div>

          <div className="rounded-lg border border-border bg-surface p-4">
            <h3 className="text-sm font-semibold text-ink">{t('topological.title')}</h3>
            {nodes.length === 0 ? (
              <p className="mt-2 text-xs text-ink-muted">{t('topological.empty')}</p>
            ) : topo ? (
              <ol className="mt-2 flex flex-col gap-1 text-xs text-ink-muted">
                {topo.map((id, i) => {
                  const n = nodes.find((nd) => nd.id === id);
                  const d = n?.data as unknown as BlueprintNodeData | undefined;
                  return (
                    <li key={id} className="flex items-baseline gap-2">
                      <span className="w-5 font-mono text-ink-subtle">{i + 1}.</span>
                      <span className="text-ink">{d?.name ?? id}</span>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="mt-2 text-xs text-danger">cycle detected</p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

// Keep a silent reference so `portTypeLabel` stays imported for
// future inspector tooltip work without triggering an unused warning.
void portTypeLabel;
